import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { listCustomers, type Customer } from '../../api/customers'
import { DataTable, type Column } from '@/components/DataTable'
import { FilterBar } from '@/components/FilterBar'
import { PageHeader } from '@/components/PageHeader'

const CUSTOMER_FILTERS = [
  { id: 'alle', label: 'Alle' },
  { id: 'mit_firma', label: 'Mit Firma' },
  { id: 'ohne_firma', label: 'Ohne Firma' },
]

export default function CustomerList() {
  const navigate = useNavigate()
  const [activeFilter, setActiveFilter] = useState('alle')
  const [search, setSearch] = useState('')
  const [minId, setMinId] = useState<number | undefined>()
  const [pageHistory, setPageHistory] = useState<number[]>([])

  const has_company =
    activeFilter === 'mit_firma' ? true :
    activeFilter === 'ohne_firma' ? false :
    undefined

  function handleFilterChange(id: string) {
    setActiveFilter(id)
    setMinId(undefined)
    setPageHistory([])
  }

  function handleSearch(value: string) {
    setSearch(value)
    setMinId(undefined)
    setPageHistory([])
  }

  const { data, isLoading, isError } = useQuery({
    queryKey: ['customers', activeFilter, search, minId],
    queryFn: () => listCustomers({ search: search || undefined, has_company, min_id: minId }),
    staleTime: 30_000,
  })

  const columns: Column<Customer>[] = [
    {
      key: 'name',
      header: 'Kunde',
      cell: (c) => (
        <div>
          <div className="font-medium">{c.username ?? c.email}</div>
          {c.username && (
            <div className="text-xs text-muted-foreground">{c.email}</div>
          )}
        </div>
      ),
    },
    { key: 'company', header: 'Firma', cell: (c) => c.company ?? '—' },
    { key: 'phone', header: 'Telefon', cell: (c) => c.phone ?? '—' },
  ]

  return (
    <div className="p-6">
      <PageHeader title="Kunden" />
      <FilterBar
        filters={CUSTOMER_FILTERS}
        active={activeFilter}
        onChange={handleFilterChange}
        search={search}
        onSearch={handleSearch}
        placeholder="Name, E-Mail, Firma…"
      />
      <div className="mt-4">
        {isError && (
          <p className="text-sm text-destructive mb-3">Fehler beim Laden.</p>
        )}
        <DataTable
          data={data?.customers ?? []}
          columns={columns}
          rowKey={(c) => c.id}
          onRowClick={(c) => navigate(`/customers/${c.id}`)}
          isLoading={isLoading}
          emptyMessage="Keine Kunden gefunden."
          pagination={data ? {
            hasMore: data.has_more,
            onNext: () => {
              if (data.next_min_id) {
                setPageHistory((h) => [...h, minId ?? 0])
                setMinId(data.next_min_id)
              }
            },
            onPrev: () => {
              const prev = pageHistory[pageHistory.length - 1]
              setPageHistory((h) => h.slice(0, -1))
              setMinId(prev === 0 ? undefined : prev)
            },
            canPrev: pageHistory.length > 0,
            count: data.customers.length,
          } : undefined}
        />
      </div>
    </div>
  )
}
